// Browser-side Web Push helpers. No React, no React Query — these are pure
// async wrappers around the Service Worker / PushManager / Notification APIs
// so the React layer can stay declarative.
//
// Subscription endpoints are per-browser-per-device — a user logged in on
// desktop Chrome and mobile Safari has two distinct PushSubscription rows.
// The DB unique key (user_id, endpoint) reflects that.

export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager'  in window &&
    'Notification' in window
  )
}

export function getPermissionState(): NotificationPermission {
  if (typeof Notification === 'undefined') return 'default'
  return Notification.permission
}

export async function requestPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied'
  return Notification.requestPermission()
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null
  const reg = await navigator.serviceWorker.ready
  return reg.pushManager.getSubscription()
}

export async function subscribeToPush(): Promise<PushSubscription> {
  const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined
  if (!vapidKey) {
    throw new Error(
      'VITE_VAPID_PUBLIC_KEY is not set — add it to your .env and restart the dev server.',
    )
  }

  const reg = await navigator.serviceWorker.ready

  // Reuse an existing subscription if one already exists in this browser:
  // calling subscribe() with the same applicationServerKey would no-op, but
  // calling with a DIFFERENT key would throw — so the safer pattern is to
  // surface the existing one and only create a new one when none is present.
  const existing = await reg.pushManager.getSubscription()
  if (existing) return existing

  return reg.pushManager.subscribe({
    userVisibleOnly:      true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey),
  })
}

export async function unsubscribeFromPush(): Promise<boolean> {
  const sub = await getCurrentSubscription()
  if (!sub) return false
  return sub.unsubscribe()
}

// Canonical VAPID key conversion. Web Push expects a Uint8Array but VAPID
// public keys are distributed as URL-safe base64 (`-` and `_` instead of
// `+` and `/`, no `=` padding). The unpadding + char swap below is the
// MDN-documented approach.
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const padded  = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw     = window.atob(padded)
  // Allocate a plain ArrayBuffer (not a SharedArrayBuffer-compatible
  // ArrayBufferLike) so the resulting view satisfies BufferSource for
  // pushManager.subscribe under TS strict mode.
  const buf = new ArrayBuffer(raw.length)
  const out = new Uint8Array(buf)
  for (let i = 0; i < raw.length; i++) {
    out[i] = raw.charCodeAt(i)
  }
  return out
}
